import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Merges and deduplicates Tailwind CSS class names.
 *
 * Combines multiple class values using `clsx` for conditional/array support,
 * then passes the result through `tailwind-merge` to resolve conflicting
 * Tailwind utility classes (e.g., `"px-2 px-4"` becomes `"px-4"`).
 *
 * @param inputs - Class values (strings, arrays, objects, or falsy values) to merge.
 * @returns A single deduplicated class string.
 */
export function cn(...inputs: ClassValue[]): string {
	return twMerge(clsx(inputs));
}

/**
 * Returns a debounced version of `func` that delays invocation until `wait`
 * milliseconds have elapsed since the last call. Preserves the latest `this`
 * and arguments.
 *
 * @param func - The function to debounce.
 * @param wait - The delay in milliseconds.
 * @returns A debounced wrapper around `func`.
 */
export function debounce<T extends (...args: never[]) => void>(
	func: T,
	wait: number,
): (...args: Parameters<T>) => void {
	let timeout: ReturnType<typeof setTimeout> | null = null;
	return function (this: unknown, ...args: Parameters<T>) {
		if (timeout) {
			clearTimeout(timeout);
		}
		timeout = setTimeout(() => func.apply(this, args), wait);
	};
}
